/**
 * EventBadge: Displays event status dots for calendar days.
 * Priority: red (cancelled) > blue (confirmed) > gray (tentative) > amber (reminder).
 * Shows up to 3 dots + "+n" if more.
 */

import React from 'react';

interface EventBadgeProps {
  color: 'red' | 'blue' | 'gray' | 'amber' | null;
  count: number;
  statuses: Record<string, number>;
}

export const EventBadge: React.FC<EventBadgeProps> = ({ color, count, statuses }) => {
  if (!color || count === 0) return null;

  const colorClasses = {
    red: 'bg-red-500',
    blue: 'bg-blue-500',
    gray: 'bg-gray-400',
    amber: 'bg-amber-500',
  };

  const dotClass = colorClasses[color];

  // Show up to 3 dots, plus a "+n" if more
  const dotsToShow = Math.min(count, 3);
  const overflow = count > 3 ? count - 3 : 0;

  return (
    <div className="absolute bottom-0.5 right-0.5 flex items-center gap-0.5">
      {Array.from({ length: dotsToShow }).map((_, i) => (
        <div
          key={i}
          className={`w-1.5 h-1.5 rounded-full ${dotClass} shadow-[0_0_2px_rgba(0,0,0,0.25)]`}
        />
      ))}
      {overflow > 0 && (
        <span className="text-[7px] font-bold text-foreground/70 leading-none ml-0.5">
          +{overflow}
        </span>
      )}
    </div>
  );
};
